import { useState, useEffect, useMemo } from 'react';
import DATimeline from './DATimeline';
import { formatDADate } from '../lib/daTimeline';
import { sStyle, catFromCategory, sAddr, cShort, dAgo, hasValidCoords } from '../lib/utils';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { supabase } from '../supabaseClient';
import { showToast } from '../lib/toast';
import { fetchDAProDetail, proDataCache } from '../lib/fetchDAProDetail';
import { useDANote } from '../hooks/useDANote';
import ProIntelligence, { hasProIntelligenceContent, isProSnapshotMissing } from './ProIntelligence';
import HelpTip from './HelpTip';
import PipelineStageSelector, { PipelineBadge } from './PipelineStageSelector';
import { GMAP_KEY } from '../lib/constants';
import { getCouncilTrackerUrl, councilLinkLabel } from '../lib/councilLinks';
import CouncilSearchHelp from './ui/CouncilSearchHelp';
import { trackDAEvent } from '../lib/analytics';
import { displayDALocation, displayDAIdentifier, isEarlyPortalSignal } from '../lib/daDisplay';
import { logProIntelligenceGap, logSlowProFetch } from '../lib/proGapEvents';
import { refreshAppShell } from '../lib/appRecovery';
import { reportClientDiagnostic } from '../lib/clientDiagnostics';
import { getPipelineCost } from '../lib/pipelineCost';
import { getDwellings, getStoreys } from '../lib/daMeasurements';
import { isSupabaseCircuitError } from '../lib/supabaseCircuitBreaker';
import { getCategoryLabel, resolveProposalType } from '../lib/daCategories';

function formatCost(cost) {
  if (!cost || cost === 0) return null;
  if (cost >= 1000000) return `$${(cost / 1000000).toFixed(1)}M`;
  if (cost >= 1000) return `$${(cost / 1000).toFixed(0)}K`;
  return `$${cost.toLocaleString()}`;
}

function formatCostFull(cost) {
  if (!cost || cost === 0) return null;
  return `$${Number(cost).toLocaleString()}`;
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatSignalDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function truncateEvidenceQuote(value, max = 150) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3).trim()}...`;
}

function proRetryDelayMs(error) {
  if (isSupabaseCircuitError(error)) {
    const retryAfterMs = Number(error?.retryAfterMs || 0);
    if (Number.isFinite(retryAfterMs) && retryAfterMs > 0) {
      return Math.min(30_000, Math.max(900, retryAfterMs + 250));
    }
  }
  return 900;
}

const PRO_AUTO_RETRY_LIMIT = 2;
const PRO_RETRYING_MESSAGE = 'Pro Intelligence is taking longer than expected. Retrying...';

function getMoreInfoUrl(da, proData) {
  return getCouncilTrackerUrl(da, proData);
}

const SEARCH_CONTEXT_STOP_WORDS = new Set(['a', 'an', 'and', 'at', 'by', 'for', 'from', 'in', 'of', 'on', 'the', 'to', 'with']);

function normaliseSearchContext(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function searchTerms(value) {
  return normaliseSearchContext(value)
    .split(' ')
    .filter(term => term.length >= 2 && !SEARCH_CONTEXT_STOP_WORDS.has(term));
}

export function getSearchContactMatch(app, searchText) {
  const terms = searchTerms(searchText);
  if (!terms.length) return null;

  const contacts = [
    { role: 'Applicant', name: app?.applicant_name, company: app?.applicant_company },
    { role: 'Planner', name: app?.planner_name, company: app?.planner_company },
    { role: 'Builder', name: app?.builder_name, company: app?.builder_company },
    { role: 'Architect', name: app?.architect_name, company: app?.architect_company },
    { role: 'Certifier', name: app?.certifier_name, company: app?.certifier_company },
    { role: 'Engineer', name: app?.engineer_name, company: app?.engineer_company },
    { role: 'Surveyor', name: app?.surveyor_name, company: app?.surveyor_company },
    { role: 'Owner', name: app?.owner_name },
  ];

  for (const contact of contacts) {
    const haystack = normaliseSearchContext([contact.name, contact.company].filter(Boolean).join(' '));
    if (!haystack || !terms.every(term => haystack.includes(term))) continue;
    const primary = contact.name || contact.company;
    const company = contact.name && contact.company ? contact.company : null;
    if (!primary) continue;
    return { role: contact.role, label: company ? `${primary} — ${company}` : primary };
  }

  return null;
}

// onRemove     — optional: renders a × remove button with confirm dialog (used in Tracked DAs)
// trackedSince — optional: shows "Tracking since DATE" in expanded section (used in Tracked DAs)
// onTrack      — optional: when omitted, the Track button is not rendered
// updateStage  — optional: pipeline stage updater from usePipeline (Pro only, Tracked DAs)
// onOpenDA     — optional: when provided, tapping opens DADetailView instead of inline expand
export default function DACard({ app, exp, onTog, onTrack, tracked, theme, onRemove, trackedSince, onShowOnMap, updateStage, onOpenDA, searchText = '' }) {
  const [pressed, setPressed]           = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [sharePulsed, setSharePulsed]   = useState(false);
  const [proData, setProData]           = useState(null);
  const [proLoading, setProLoading]     = useState(false);
  const [proError, setProError]         = useState(null);
  const [proRetryState, setProRetryState] = useState({ scope: '', count: 0 });
  const [mapPreviewFailed, setMapPreviewFailed] = useState(false);
  const isMobile = useIsMobile();
  const { user, loading: authLoading } = useAuth();
  const { isPro, tier, loading: subLoading } = useSubscription();
  const note = useDANote(user?.id, app.id, exp);
  const lat = Number(app.latitude);
  const lng = Number(app.longitude);
  const hasCoords = hasValidCoords(app);
  const appId = app.id;
  const appCouncil = app.council;
  const appDaNumber = app.da_number;
  const appSource = app.source;
  const appIsPrimary = app.is_primary;
  const appParentDaId = app.parent_da_id;
  const userId = user?.id;
  const proRetryScope = `${userId || 'anonymous'}:${appId}`;
  const proRetry = proRetryState.scope === proRetryScope ? proRetryState.count : 0;
  const diagnosticUser = useMemo(() => userId ? { id: userId } : null, [userId]);
  const proDiagnosticDa = useMemo(() => ({
    id: appId,
    council: appCouncil,
    da_number: appDaNumber,
    source: appSource,
    is_primary: appIsPrimary,
    parent_da_id: appParentDaId,
  }), [appId, appCouncil, appDaNumber, appSource, appIsPrimary, appParentDaId]);

  useEffect(() => {
    setMapPreviewFailed(false);
  }, [appId, app.latitude, app.longitude]);

  // Fetch Pro Intelligence data when expanded by a Pro user.
  // Uses cancelled flag + async/await to prevent stale writes when card changes mid-fetch.
  // proRetry state increments trigger a retry when the user taps the error message.
  useEffect(() => {
    if (!exp || !isPro || subLoading || authLoading || !userId) {
      if (!exp || !isPro || !userId) {
        setProLoading(false);
        setProError(null);
      }
      return;
    }

    if (!navigator.onLine) {
      setProLoading(false);
      setProError('You\'re offline — could not load Pro data.');
      reportClientDiagnostic('pro_fetch_offline', {
        severity: 'warning',
        user: diagnosticUser,
        surface: 'card',
        page: 'dashboard',
        da: proDiagnosticDa,
        message: 'Phone was offline when Pro intelligence was requested',
        metadata: { tier },
      });
      return;
    }

    // Serve from cache immediately — skip loading state entirely
    const cacheKey = `${userId || 'user'}:${appId}`;
    const cached = proDataCache.get(cacheKey);
    if (cached) {
      setProData(cached);
      setProLoading(false);
      setProError(null);
      return;
    }

    let cancelled = false;
    setProData(null);
    setProError(null);
    setProLoading(true);

    const fetchProData = async () => {
      const startedAt = Date.now();
      let retryScheduled = false;
      try {
        const data = await fetchDAProDetail(appId, {
          label: 'DACard',
          cacheScope: userId,
          bypassCache: proRetry > 0,
        });
        if (cancelled) return;
        const elapsedMs = logSlowProFetch(appId, startedAt, {
          surface: 'card',
          council: appCouncil,
          da_number: appDaNumber,
          source: appSource,
          is_primary: appIsPrimary,
          parent_da_id: appParentDaId,
          has_parent: Boolean(appParentDaId),
        });
        if (elapsedMs >= 4000) {
          reportClientDiagnostic('slow_pro_fetch', {
            severity: 'warning',
            user: diagnosticUser,
            surface: 'card',
            page: 'dashboard',
            da: proDiagnosticDa,
            message: `Pro intelligence was slow (${elapsedMs}ms)`,
            timing: { elapsed_ms: elapsedMs },
            metadata: { tier },
          });
        }
        setProData(data);
        if (isProSnapshotMissing(data)) {
          reportClientDiagnostic('pro_snapshot_pending', {
            severity: 'info',
            user: diagnosticUser,
            surface: 'card',
            page: 'dashboard',
            da: proDiagnosticDa,
            message: 'Pro Intelligence snapshot is queued for preparation',
            metadata: {
              tier,
              snapshot_source: data?.snapshot_source,
              pro_snapshot_missing: true,
            },
          });
        } else if (!hasProIntelligenceContent(data, tier) && !(data?.trusted_parties?.length > 0)) {
          logProIntelligenceGap(appId, 'empty_pro_payload', {
            surface: 'card',
            council: appCouncil,
            da_number: appDaNumber,
          });
          reportClientDiagnostic('empty_pro_payload', {
            severity: 'warning',
            user: diagnosticUser,
            surface: 'card',
            page: 'dashboard',
            da: proDiagnosticDa,
            message: 'Pro intelligence returned no usable content',
            metadata: {
              tier,
              trusted_count: data?.trusted_parties?.length || 0,
              has_supply_chain: Array.isArray(data?.supply_chain) && data.supply_chain.length > 0,
              snapshot_source: data?.snapshot_source,
            },
          });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[DACard] Pro data fetch failed:', err.message);
        logProIntelligenceGap(appId, 'pro_fetch_failed', {
          surface: 'card',
          council: appCouncil,
          da_number: appDaNumber,
          message: err.message,
        });
        reportClientDiagnostic('pro_fetch_failed', {
          severity: 'error',
          user: diagnosticUser,
          surface: 'card',
          page: 'dashboard',
          da: proDiagnosticDa,
          message: err.message || 'Pro intelligence failed to load',
          metadata: { retry: proRetry, tier },
          error: err,
        });
        const willRetry = proRetry < PRO_AUTO_RETRY_LIMIT && navigator.onLine;
        if (willRetry) {
          retryScheduled = true;
          setProLoading(false);
          setProError(PRO_RETRYING_MESSAGE);
          setTimeout(() => {
            if (!cancelled) {
              setProRetryState(state => ({
                scope: proRetryScope,
                count: state.scope === proRetryScope ? state.count + 1 : 1,
              }));
            }
          }, proRetryDelayMs(err));
        } else {
          setProError(isSupabaseCircuitError(err)
            ? 'Supabase is cooling down. Try again in a moment.'
            : `Could not load Pro data${err?.message ? `: ${err.message}` : ''}`);
        }
      } finally {
        if (!cancelled && !retryScheduled) setProLoading(false);
      }
    };

    fetchProData();

    return () => {
      cancelled = true;
    };
  }, [
    exp,
    isPro,
    subLoading,
    authLoading,
    userId,
    appId,
    appCouncil,
    appDaNumber,
    appSource,
    appIsPrimary,
    appParentDaId,
    diagnosticUser,
    proDiagnosticDa,
    proRetryScope,
    proRetry,
    tier,
  ]);

  const handleShare = async (e) => {
    e.stopPropagation();
    const appNum = app.da_number;
    if (!appNum) return;
    const shareUrl = `https://sitescopeapp.com/da?app=${encodeURIComponent(appNum)}&council=${encodeURIComponent(app.council || '')}`;
    trackDAEvent('da_share', app, { method: navigator.share ? 'native_share' : 'clipboard', surface: 'card' }, { page: 'dashboard', source: 'share_button' });

    // Fire-and-forget analytics
    if (user) {
      supabase.from('share_events').insert({
        event_type:                 'share',
        da_id:                      app.id,
        council_application_number: appNum,
        council:                    app.council,
        shared_by:                  user.id,
      }).then(({ error }) => { if (error) console.error('[SiteScope] share_events insert failed:', error.message); });
    }

    setSharePulsed(true);
    setTimeout(() => setSharePulsed(false), 600);

    if (navigator.share) {
      navigator.share({
        title: `DA ${appNum}`,
        text:  `${displayAddress} — ${app.status || ''}`,
        url:   shareUrl,
      }).catch(() => {});
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast('Link copied to clipboard');
      } catch {
        showToast('Copy this link: ' + shareUrl);
      }
    }
  };
  const ss = sStyle(app.status), cat = catFromCategory(app.application_type || app.category);
  const pipelineCost = getPipelineCost(app);
  const dwellings = getDwellings(app);
  const storeys = getStoreys(app);
  const cost = formatCost(pipelineCost);
  const moreInfoUrl = getMoreInfoUrl(app, proData);
  const devTypes = Array.isArray(app.development_type) ? app.development_type : [];
  const hasAnyProContent = hasProIntelligenceContent(proData, tier) || (proData?.trusted_parties?.length > 0);
  const searchContactMatch = getSearchContactMatch(app, searchText);
  const earlyPortalSignal = isEarlyPortalSignal(app);
  const earlySignalDate = formatSignalDate(app.created_at);
  const displayIdentifier = displayDAIdentifier(app);
  const displayAddress = displayDALocation(app, displayIdentifier);
  const proposalType = resolveProposalType(app);
  const proposalTypeEvidence = app.proposal_type_ai ? truncateEvidenceQuote(app.proposal_type_ai_evidence) : '';

  const handleCardClick = (e) => {
    if (e.target.closest('textarea, input, [contenteditable]')) return;
    if (onOpenDA) { onOpenDA(app); return; }
    onTog?.();
  };

  return (
    <div
      onClick={handleCardClick}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      role="button"
      aria-label={`View details for ${displayAddress || app.da_number}`}
      tabIndex={0}
      onKeyDown={e => { if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT' || e.target.isContentEditable) { if (e.key === ' ' || e.key === 'Enter') e.stopPropagation(); return; } if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(e); } }}
      style={{
        background: theme.bgCard, border: `1px solid ${theme.border}`,
        borderRadius: 12, padding: isMobile ? "14px" : "16px",
        cursor: "pointer", position: "relative",
        transform: pressed ? "scale(0.98)" : "scale(1)",
        transition: "transform 0.1s",
        boxShadow: "0 2px 12px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25)",
        marginBottom: 12,
      }}
    >
      {/* Remove button (Tracked DAs) */}
      {onRemove && !confirmRemove && (
        <button
          onClick={e => { e.stopPropagation(); setConfirmRemove(true); }}
          aria-label={`Remove ${app.da_number || 'this DA'} from Tracked DAs`}
          style={{position:"absolute",top:"10px",right:"10px",background:"none",border:"none",color:theme.textMuted,fontSize:"var(--text-lg)",cursor:"pointer",padding:"4px",lineHeight:1}}
        >×</button>
      )}

      {/* Row 1: DA# + CategoryPill | Cost */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,paddingRight:onRemove?"28px":0}}>
        <div style={{display:"flex",alignItems:"center",gap:6,flex:1,minWidth:0,flexWrap:"wrap"}}>
          <span style={{fontSize:13,fontFamily:"'JetBrains Mono',monospace",color:theme.textMuted}}>{displayIdentifier}</span>
          <span style={{color:cat.c,fontSize:10,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase"}}>{cat.l}</span>
        </div>
        {cost && <span style={{fontSize:15,fontWeight:700,color:theme.accent,flexShrink:0,marginLeft:8}}>{cost}</span>}
      </div>

      {/* Row 2: Address */}
      <div
        onDoubleClick={e=>{e.stopPropagation();if(onShowOnMap)onShowOnMap(app);}}
        style={{color:theme.text,fontSize:16,fontWeight:600,marginBottom:6,lineHeight:1.3,fontFamily:"'DM Sans',sans-serif",cursor:onShowOnMap?"pointer":"default"}}
        title={onShowOnMap?"Double-tap to show on map":undefined}
      >{sAddr(displayAddress)}</div>

      {app.description && !exp && <p style={{ color: theme.textMuted, fontSize: 14, lineHeight: 1.45, margin: '6px 0 10px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{app.description}</p>}
      {/* Row 3: Meta | StatusBadge */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
        <span style={{color:theme.textMuted,fontSize:12,flex:1,minWidth:0}}>
          {cShort(app.council)} · {formatDADate(app.lodged_date) ? `Lodged ${formatDADate(app.lodged_date)} · ${dAgo(app.lodged_date)}` : `Lodged date unknown · Seen ${earlySignalDate || 'recently'}`}
          {dwellings>0&&` · ${dwellings} dwelling${dwellings===1?'':'s'}`}
          {storeys>0&&` · ${storeys} storey${storeys===1?'':'s'}`}
        </span>
        <span style={{fontSize:11,fontWeight:600,padding:"3px 8px",borderRadius:4,background:ss.bg,color:ss.c,border:`1px solid ${ss.c}22`,whiteSpace:"nowrap",flexShrink:0}}>{app.status}</span>
      </div>
      {formatDADate(app.status_changed_at) && <div style={{ marginTop: 5, color: theme.textMuted, fontSize: 12 }}>
        Status change recorded {formatDADate(app.status_changed_at)}
      </div>}
      {searchContactMatch && (
        <div style={{marginTop:8,fontSize:12,color:theme.textMuted,lineHeight:1.35}}>
          <span style={{color:theme.accent,fontWeight:700,textTransform:'uppercase',letterSpacing:0.6,fontSize:10}}>{searchContactMatch.role}</span>
          <span style={{color:theme.text}}> {searchContactMatch.label}</span>
        </div>
      )}

      {/* Pipeline badge — Pro users, tracked DAs, non-watching stage */}
      {isPro && tracked && app.pipeline_stage && app.pipeline_stage !== 'watching' && (
        <PipelineBadge app={app} />
      )}

      {/* Action buttons — only in non-detail-view (list/inline expand) mode */}
      {!onOpenDA && <div style={{display:"flex",gap:"12px",marginTop:"8px",flexWrap:"wrap",alignItems:"center"}} onClick={e=>e.stopPropagation()}>
        {moreInfoUrl && (
          <a
            href={moreInfoUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e=>{ e.stopPropagation(); trackDAEvent('da_council_click', app, { url: moreInfoUrl, surface: 'card' }, { page: 'dashboard', source: 'council_link' }); }}
            style={{fontSize:"var(--text-xs)",color:theme.accent,textDecoration:"none",lineHeight:1}}
          >{councilLinkLabel(moreInfoUrl, app, proData)}</a>
        )}
        {onTrack && (
          <button data-onboarding="track-button" onClick={e=>{e.stopPropagation();onTrack(app);}} style={{fontSize:"var(--text-xs)",color:tracked?"#2a9d5a":"#5580cc",background:"none",border:"none",cursor:"pointer",padding:0,fontFamily:"inherit",lineHeight:1}}>{tracked?"✓ Tracking":"+ Track this DA"}</button>
        )}
        <button
          onClick={handleShare}
          aria-label="Share this DA"
          title="Share"
          style={{
            background:"none",border:"none",cursor:"pointer",padding:0,lineHeight:1,
            color: sharePulsed ? "#d4a84a" : theme.textMuted,
            transition:"color 0.3s",
            fontSize:"var(--text-xs)",fontFamily:"inherit",
          }}
        >Share</button>
      </div>}

      {!onOpenDA && <CouncilSearchHelp app={app} proData={proData} url={moreInfoUrl} color={theme.textMuted} accent={theme.accent} />}

      {/* Remove confirm dialog */}
      {onRemove && confirmRemove && (
        <div onClick={e=>e.stopPropagation()} style={{display:"flex",gap:"8px",marginTop:"10px",paddingTop:"10px",borderTop:`1px solid ${theme.border}`}}>
          <button onClick={()=>onRemove(app)} style={{padding:"6px 12px",background:"#dc2626",color:"#fff",border:"none",borderRadius:"6px",fontSize:"var(--text-sm)",cursor:"pointer",fontFamily:"inherit"}}>Remove</button>
          <button onClick={()=>setConfirmRemove(false)} style={{padding:"6px 12px",background:"transparent",color:theme.textMuted,border:`1px solid ${theme.border}`,borderRadius:"6px",fontSize:"var(--text-sm)",cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
        </div>
      )}

      {exp && (
        <div style={{marginTop:"10px",padding:"10px 12px",background:`${theme.accent}08`,borderRadius:"6px",borderLeft:`3px solid ${theme.accent}4d`}}>

          {/* Static map preview */}
          {hasCoords && (() => {
            const params = new URLSearchParams({
              center: `${lat},${lng}`,
              zoom: '16',
              size: '600x200',
              scale: '2',
              maptype: 'hybrid',
              markers: `color:orange|${lat},${lng}`,
              key: GMAP_KEY,
            });
            params.append('style', 'feature:poi|visibility:off');
            params.append('style', 'feature:transit|visibility:off');
            const src = `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
            if (!GMAP_KEY || mapPreviewFailed) return null;
            return (
              <div
                onClick={e => { e.stopPropagation(); onShowOnMap?.(app); }}
                style={{
                  borderRadius: 8, overflow: 'hidden', marginBottom: 10, width: '100%', height: 120,
                  position: 'relative', background: '#1e4060', cursor: onShowOnMap ? 'pointer' : 'default',
                  border: `1px solid ${theme.border}`,
                }}
              >
                <img
                  src={src}
                  alt="DA location map"
                  width="100%"
                  height="120"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => setMapPreviewFailed(true)}
                />
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  background: `${theme.bgCard}dd`, borderRadius: 6, padding: '4px 9px',
                }}>
                  <span style={{ color: theme.accent, fontSize: 10, fontWeight: 600 }}>Tap to open map →</span>
                </div>
              </div>
            );
          })()}
          {!hasCoords && (
            <div style={{
              borderRadius: 8, marginBottom: 10, padding: '12px 14px',
              background: '#101827', border: `1px solid ${theme.border}`,
              color: theme.textMuted, fontSize: 12, lineHeight: 1.45,
            }}>
              Map unavailable until this DA has verified coordinates.
            </div>
          )}

          {/* Development type */}
          {devTypes.length > 0 && (
            <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,marginBottom:"8px"}}>
              <span style={{color:theme.text,fontWeight:"600"}}>Type: </span>{devTypes.join(', ')}
            </div>
          )}

          {/* Key details */}
          {(pipelineCost > 0 || dwellings > 0 || storeys > 0) && (
            <div style={{display:"flex",gap:"16px",marginBottom:"8px",flexWrap:"wrap"}}>
              {pipelineCost > 0 && (
                <div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:"2px"}}>Est. Cost</div>
                  <div style={{fontSize:"var(--text-base)",fontWeight:"700",color:theme.accent}}>{formatCostFull(pipelineCost)}</div>
                </div>
              )}
              {dwellings > 0 && (
                <div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:"2px"}}>New Dwellings</div>
                  <div style={{fontSize:"var(--text-base)",fontWeight:"700",color:theme.text}}>{dwellings}</div>
                </div>
              )}
              {storeys > 0 && (
                <div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:"2px"}}>Storeys</div>
                  <div style={{fontSize:"var(--text-base)",fontWeight:"700",color:theme.text}}>{storeys}</div>
                </div>
              )}
            </div>
          )}

          <DATimeline app={app} theme={theme} />

          {/* Dates */}
          {(app.lodged_date || earlyPortalSignal || app.determination_date || app.determined_date) && (
            <div style={{display:"flex",gap:"16px",marginBottom:"8px",flexWrap:"wrap"}}>
              {app.lodged_date && (
                <div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:"2px"}}>Lodged</div>
                  <div style={{fontSize:"var(--text-sm)",color:theme.text}}>{formatDate(app.lodged_date)}</div>
                </div>
              )}
              {!app.lodged_date && earlyPortalSignal && app.created_at && (
                <div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:"2px"}}>Detected</div>
                  <div style={{fontSize:"var(--text-sm)",color:theme.text}}>{formatDate(app.created_at)}</div>
                </div>
              )}
              {(app.determination_date || app.determined_date) && (
                <div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:"2px"}}>Determined</div>
                  <div style={{fontSize:"var(--text-sm)",color:theme.text}}>{formatDate(app.determination_date || app.determined_date)}</div>
                </div>
              )}
            </div>
          )}

          {/* References */}
          {(app.lot_id_string || trackedSince) && (
            <div style={{display:"flex",gap:"16px",marginBottom:"8px",flexWrap:"wrap"}}>
              {app.lot_id_string && (
                <div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:"2px"}}>Lot/Plan</div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.text,fontFamily:"'JetBrains Mono',monospace"}}>{app.lot_id_string}</div>
                </div>
              )}
              {trackedSince && (
                <div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:"2px"}}>Tracking Since</div>
                  <div style={{fontSize:"var(--text-xs)",color:theme.text}}>{trackedSince}</div>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {app.description && (
            <div style={{fontSize:"var(--text-sm)",color:theme.textMuted,lineHeight:"1.6",marginTop:"4px",marginBottom:"8px"}}>{app.description}</div>
          )}

          {proposalTypeEvidence && (
            <div style={{fontSize:"var(--text-xs)",color:theme.textMuted,lineHeight:1.45,marginTop:"8px",marginBottom:"8px",padding:"8px 10px",border:`1px solid ${theme.border}`,borderRadius:"6px",background:"rgba(10,27,54,0.35)"}}>
              <span style={{color:theme.accent,fontWeight:700}}>Categorised as {getCategoryLabel(proposalType)}</span>
              <span> - "{proposalTypeEvidence}"</span>
            </div>
          )}

          {/* Private note */}
          {user && (
            <div style={{marginTop:"14px",paddingTop:"12px",borderTop:`1px solid ${theme.border}`}} onClick={e=>e.stopPropagation()}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"6px"}}>
                <span style={{fontSize:"var(--text-xs)",color:theme.textMuted,textTransform:"uppercase",letterSpacing:"0.8px",fontWeight:"600"}}>Private Note</span>
                <span style={{
                  fontSize:"var(--text-xs)",color:"#2a9d5a",
                  opacity: note.saved ? 1 : 0,
                  transition:"opacity 0.4s ease",
                  pointerEvents:"none",
                }}>{note.saved ? 'Saved' : ''}</span>
              </div>
              {note.error && <div role="alert" style={{ color: theme.textMuted }}>{note.error}{note.loaded && <button onClick={note.handleBlur}>Retry save</button>}</div>}
              <textarea
                disabled={!note.loaded}
                aria-label="Private note and next step"
                value={note.text}
                onChange={e => note.handleChange(e.target.value)}
                placeholder="Add a private note…"
                rows={3}
                style={{
                  width:"100%",
                  boxSizing:"border-box",
                  background: theme.bgCard,
                  border:`1px solid ${theme.border}`,
                  borderRadius:"6px",
                  color:"#e8e0d8",
                  fontSize:"var(--text-input, 16px)",
                  fontFamily:"'DM Sans','Helvetica Neue',sans-serif",
                  lineHeight:1.6,
                  padding:"8px 10px",
                  resize:"vertical",
                  outline:"none",
                  transition:"border-color 0.15s",
                }}
                onFocus={e => e.target.style.borderColor=theme.accent + "66"}
                onBlur={e => { e.target.style.borderColor=theme.border; note.handleBlur(); }}
              />
            </div>
          )}


          {/* Pipeline Stage Selector — Pro users, tracked DAs only */}
          {isPro && tracked && updateStage && (
            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: `1px solid ${theme.border}` }}>
              <PipelineStageSelector app={app} updateStage={updateStage} />
            </div>
          )}

          {/* Pro Intelligence — only for Pro/Trade/Business users */}
          {isPro && proLoading && (
            <>
              <style>{`@keyframes proShimmer { 0%,100% { opacity: 0.15; } 50% { opacity: 0.4; } }`}</style>
              <div style={{
                height: 2, borderRadius: 2, marginTop: 14,
                background: '#d4a84a',
                animation: 'proShimmer 1.8s ease-in-out infinite',
              }} />
            </>
          )}
          {isPro && !proLoading && proError && (
            <div style={{ marginTop: '12px', padding: '8px 0 2px', textAlign: 'center', fontFamily: "'DM Sans',sans-serif" }}>
              <div style={{ fontSize: 'var(--text-xs)', color: '#9ca3af', marginBottom: 8 }}>{proError}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setProRetryState(state => ({ scope: proRetryScope, count: state.scope === proRetryScope ? state.count + 1 : 1 }))} style={{ background: '#d4a84a', color: '#081525', border: 'none', borderRadius: 999, padding: '6px 11px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>Retry</button>
                <button type="button" onClick={refreshAppShell} style={{ background: 'transparent', color: '#9ca3af', border: `1px solid ${theme.border}`, borderRadius: 999, padding: '6px 11px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Repair app</button>
              </div>
            </div>
          )}
          {isPro && !proLoading && !proError && (hasAnyProContent || proData) && (
            <div style={{ position: 'relative', animation: 'proFadeIn 0.3s ease' }}>
              <style>{`@keyframes proFadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }`}</style>
              <ProIntelligence appRecord={app} proData={proData} theme={theme} tier={tier} trustedParties={proData?.trusted_parties || []} showEmpty />
              <HelpTip id="pro-intelligence" position="top">
                Pro data: full supply chain, contacts, and planning details.
              </HelpTip>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
